import { X } from 'lucide-react';
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL } from '../../config/auth0';
import { startAuth0PKCE } from '../../utils/auth0pkce';
import { Product } from '../../pages/leasing-store/types';

interface LeasingLoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  requestType: 'leasing' | 'location' | 'achat' | 'cash';
  returnToProductDetail?: boolean;
}

export function LeasingLoginPromptModal({ 
  isOpen, 
  onClose, 
  product, 
  requestType, 
  returnToProductDetail = false 
}: LeasingLoginPromptModalProps) {
  if (!isOpen) return null;
  
  const handleLogin = () => {
    // Stocker les informations du produit et du type de demande
    localStorage.setItem('selected_product', JSON.stringify({
      id: product.id,
      name: product.name,
      requestType: requestType
    }));
    
    // Stocker l'information que l'utilisateur doit être redirigé vers la page d'où il vient après connexion
    // ou vers le détail du produit s'il était sur la liste
    const redirectPath = returnToProductDetail 
      ? `/leasing-store/product/${product.id}` 
      : window.location.pathname + window.location.search;
    
    sessionStorage.setItem('auth0_redirect_after_login', redirectPath);
    
    // Rediriger vers Auth0 pour la connexion avec PKCE
    startAuth0PKCE('login', AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL);
    onClose();
  };
  
  const handleSignup = () => {
    // Stocker les informations du produit et du type de demande
    localStorage.setItem('selected_product', JSON.stringify({
      id: product.id,
      name: product.name,
      requestType: requestType
    }));
    
    // Stocker l'information que l'utilisateur doit être redirigé vers la page d'où il vient après inscription
    // ou vers le détail du produit s'il était sur la liste
    const redirectPath = returnToProductDetail 
      ? `/leasing-store/product/${product.id}` 
      : window.location.pathname + window.location.search;
    
    sessionStorage.setItem('auth0_redirect_after_login', redirectPath);
    
    // Rediriger vers Auth0 pour l'inscription avec PKCE
    startAuth0PKCE('signup', AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL);
    onClose();
  };

  // Fonction utilitaire pour obtenir le texte du type de demande
  const getRequestTypeText = () => {
    switch (requestType) {
      case 'leasing': return 'en leasing';
      case 'location': return 'en location';
      case 'achat': return 'avec paiement échelonné';
      case 'cash': return 'avec paiement cash';
      default: return '';
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-6 w-6" />
        </button>
        
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Connectez-vous pour continuer
        </h3>
        
        <div className="text-gray-600 mb-6">
          <p className="mb-2">
            Pour commander <span className="font-semibold">{product.name}</span> {getRequestTypeText()}, 
            vous devez avoir un compte Wanzo.
          </p>
          <p>Connectez-vous si vous avez déjà un compte, ou créez un compte pour commencer.</p>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={handleLogin}
            className="w-full rounded-full bg-primary hover:bg-primary-hover text-white py-2.5 font-medium transition-colors"
          >
            Se connecter
          </button>
          
          <button
            onClick={handleSignup}
            className="w-full rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white py-2.5 font-medium transition-colors"
          >
            Créer un compte
          </button>
          
          <button
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
          >
            Continuer sans compte
          </button>
        </div>
      </div>
    </div>
  );
}
